import type { FC } from "react";

import { AlertCircle, CheckCircle, InfoCircle } from "@untitledui/icons";

import { FeaturedIcon } from "@/assets/icons/featured-icon/featured-icon";
import { Avatar } from "@/components/ui/avatar/avatar";
import { Button } from "@/components/ui/buttons/button";
import { CloseButton } from "@/components/ui/buttons/close-button";
import { ProgressBar } from "@/components/ui/progress-indicators/progress-indicators";
import { cx } from "@/utils/cx";

const iconMap = {
	brand: InfoCircle,
	default: InfoCircle,
	error: AlertCircle,
	gray: InfoCircle,
	success: CheckCircle,
	warning: AlertCircle,
};

interface IconNotificationProps {
	color?: "brand" | "default" | "error" | "gray" | "success" | "warning";
	confirmLabel?: string;
	description?: string;
	dismissLabel?: string;
	hideDismissLabel?: boolean;
	icon?: FC<{ className?: string }>;
	onClose?: () => void;
	onConfirm?: () => void;
	progress?: number;
	title: string;
}

export const IconNotification = ({
	color = "default",
	confirmLabel,
	description,
	dismissLabel = "Dismiss",
	hideDismissLabel,
	icon,
	onClose,
	onConfirm,
	progress,
	title,
}: IconNotificationProps) => {
	const showProgress = typeof progress === "number";

	return (
		<div className="relative z-[var(--z-index)] flex max-w-full flex-col gap-4 rounded-xl bg-primary_alt p-4 shadow-lg ring ring-secondary_alt xs:w-[var(--width)] xs:flex-row">
			<FeaturedIcon
				color={color === "default" ? "gray" : color}
				icon={icon || iconMap[color]}
				size="md"
				theme={color === "default" ? "modern" : "outline"}
			/>

			<div
				className={cx(
					"flex flex-1 flex-col gap-3 md:pr-8",
					color !== "default" && "md:pt-0.5",
					showProgress && "gap-4",
				)}
			>
				<div className="flex flex-col gap-1">
					<p className="text-sm font-semibold text-fg-primary">{title}</p>
					{description && (
						<p className="text-sm text-fg-secondary">{description}</p>
					)}
				</div>

				{showProgress && (
					<ProgressBar
						labelPosition="bottom"
						value={progress}
						valueFormatter={(value) => `${value}% uploaded...`}
					/>
				)}
				{(confirmLabel || !hideDismissLabel) && (
					<div className="flex gap-3">
						{!hideDismissLabel && (
							<Button color="link-gray" onClick={onClose} size="sm">
								{dismissLabel}
							</Button>
						)}
						{confirmLabel && (
							<Button color="link-color" onClick={onConfirm} size="sm">
								{confirmLabel}
							</Button>
						)}
					</div>
				)}
			</div>

			<div className="absolute top-2 right-2 flex items-center justify-center">
				<CloseButton label="Dismiss" onClick={onClose} size="sm" />
			</div>
		</div>
	);
};

interface AvatarNotificationProps {
	avatar: string;
	color?: "brand" | "default" | "error" | "gray" | "success" | "warning";
	confirmLabel: string;
	content: string;
	date: string;
	dismissLabel?: string;
	hideDismissLabel?: boolean;
	icon?: FC<{ className?: string }>;
	name: string;
	onClose?: () => void;
	onConfirm?: () => void;
}

export const AvatarNotification = ({
	avatar,
	confirmLabel,
	content,
	date,
	dismissLabel = "Dismiss",
	hideDismissLabel,
	name,
	onClose,
	onConfirm,
}: AvatarNotificationProps) => {
	return (
		<div className="relative z-[var(--z-index)] flex max-w-full flex-col items-start gap-4 rounded-xl bg-primary_alt p-4 shadow-lg ring ring-secondary_alt xs:w-[var(--width)] xs:flex-row">
			<Avatar alt={name} size="md" src={avatar} status="online" />

			<div className="flex flex-col gap-3 pr-8">
				<div className="flex flex-col gap-1">
					<div className="flex items-center gap-2">
						<p className="text-sm font-semibold text-fg-primary">{name}</p>
						<span className="text-sm text-fg-quaternary">{date}</span>
					</div>
					<p className="text-sm text-fg-secondary">{content}</p>
				</div>

				<div className="flex gap-3">
					{!hideDismissLabel && (
						<Button color="link-gray" onClick={onClose} size="sm">
							{dismissLabel}
						</Button>
					)}
					{confirmLabel && (
						<Button color="link-color" onClick={onConfirm} size="sm">
							{confirmLabel}
						</Button>
					)}
				</div>
			</div>

			<div className="absolute top-2 right-2 flex items-center justify-center">
				<CloseButton label="Dismiss" onClick={onClose} size="sm" />
			</div>
		</div>
	);
};

interface ImageNotificationProps {
	confirmLabel: string;
	description: string;
	dismissLabel?: string;
	hideDismissLabel?: boolean;
	imageDesktop: string;
	imageMobile: string;
	onClose?: () => void;
	onConfirm?: () => void;
	title: string;
}

export const ImageNotification = ({
	confirmLabel,
	description,
	dismissLabel = "Dismiss",
	hideDismissLabel,
	imageDesktop,
	imageMobile,
	onClose,
	onConfirm,
	title,
}: ImageNotificationProps) => {
	return (
		<div
			className="relative z-[var(--z-index)] flex max-w-full flex-col gap-3 rounded-xl bg-primary_alt p-4 shadow-lg max-md:ring-1 max-md:ring-secondary_alt xs:w-[var(--width)] xs:flex-row xs:gap-0 md:p-0"
			style={
				{
					"--width": "496px",
				} as React.CSSProperties
			}
		>
			<div className="-my-px hidden w-40 shrink-0 overflow-hidden rounded-l-xl outline-1 -outline-offset-1 outline-black/10 md:block">
				<img
					alt="Image Mobile"
					aria-hidden="true"
					className="t size-full object-cover"
					src={imageMobile}
				/>
			</div>

			<div className="flex flex-col gap-4 rounded-r-xl bg-primary_alt md:gap-3 md:p-4 md:pl-5 md:ring-1 md:ring-secondary_alt">
				<div className="flex flex-col gap-1 pr-8">
					<p className="text-sm font-semibold text-fg-primary">{title}</p>
					<p className="text-sm text-fg-secondary">{description}</p>
				</div>

				<div className="h-40 w-full overflow-hidden rounded-md bg-secondary md:hidden">
					<img
						alt="Image Desktop"
						className="size-full object-cover"
						src={imageDesktop}
					/>
				</div>

				<div className="flex gap-3">
					{!hideDismissLabel && (
						<Button color="link-gray" onClick={onClose} size="sm">
							{dismissLabel}
						</Button>
					)}
					{confirmLabel && (
						<Button color="link-color" onClick={onConfirm} size="sm">
							{confirmLabel}
						</Button>
					)}
				</div>
			</div>

			<div className="absolute top-2 right-2 flex items-center justify-center">
				<CloseButton label="Dismiss" onClick={onClose} size="sm" />
			</div>
		</div>
	);
};
